import { apiClient } from '../httpClient';
import { handleApiError } from '../utils/errorHandler';
import { serializeFastApiQueryParams } from '../utils/queryParams';

export const dyeingService = {
  // 염색 작업 후보 목록 조회
  getDyeingCandidates: async (params = {}) => {
    try {
      const response = await apiClient.get('/mes/dyeing/v2/process/candidates', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 작업 후보 목록을 가져오는데 실패했습니다.');
    }
  },

  // 염색 프로세스 목록 조회
  getDyeingProcessList: async (params = {}, queryString = '') => {
    try {
      const url = queryString ? `/mes/dyeing/v2/process/list?${queryString}` : '/mes/dyeing/v2/process/list';
      const response = await apiClient.get(url, { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 프로세스 목록을 가져오는데 실패했습니다.');
    }
  },

  // 염색 프로세스 생성
  createDyeingProcess: async (processData) => {
    try {
      const response = await apiClient.post('/mes/dyeing/v2/process/', processData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 프로세스 생성에 실패했습니다.');
    }
  },

  // 염색 프로세스 수정
  updateDyeingProcess: async (processId, processData) => {
    try {
      const response = await apiClient.put(`/mes/dyeing/v2/process/item/${processId}`, processData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 프로세스 수정에 실패했습니다.');
    }
  },

  /** 랙 코드 저장·수정 (status 8·9, 이벤트 없음) */
  patchCheeseRack: async (productionId, { cheese_rack_code }) => {
    try {
      const response = await apiClient.patch(
        `/mes/dyeing/v2/process/item/${productionId}/cheese-rack`,
        { cheese_rack_code: String(cheese_rack_code ?? '').trim() },
        { headers: { 'Content-Type': 'application/json' } }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '랙 코드 저장에 실패했습니다.');
    }
  },

  /** 랙 코드 일괄 저장·수정 (1~50건, 전체 rollback) */
  patchCheeseRackBulk: async ({ items }) => {
    try {
      const response = await apiClient.patch(
        '/mes/dyeing/v2/process/cheese-rack/bulk',
        { items },
        { headers: { 'Content-Type': 'application/json' } }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '랙 코드 일괄 저장에 실패했습니다.');
    }
  },

  // 염색 프로세스 삭제
  deleteDyeingProcess: async (processId) => {
    try {
      const response = await apiClient.delete(`/mes/dyeing/v2/process/item/${processId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 프로세스 삭제에 실패했습니다.');
    }
  },

  // 염색 프로세스 상세 조회
  getDyeingProcessDetail: async (processId) => {
    try {
      const response = await apiClient.get(`/mes/dyeing/v2/process/item/${processId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 프로세스 상세 조회에 실패했습니다.');
    }
  },

  // v2 레시피 API (RecipeResponse: recipe_data, production_ids_by_revision 등)
  getRecipe: async (recipeId) => {
    try {
      const response = await apiClient.get(`/mes/dyeing/v2/recipe/recipe_id/${recipeId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피를 가져오는데 실패했습니다.');
    }
  },

  /** recipe_id로 주문 정보 조회 (OrderInfo: customer_id, color) */
  getOrderInfoByRecipeId: async (recipeId) => {
    try {
      const response = await apiClient.get(`/mes/dyeing/v2/recipe/order_info_by_recipe_id/${recipeId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '주문 정보를 가져오는데 실패했습니다.');
    }
  },

  // 레시피 생성 (RecipeCreate: recipe_data, current_revision, dyeing_no_list, production_ids 등)
  createRecipe: async (recipeData) => {
    try {
      const response = await apiClient.post('/mes/dyeing/v2/recipe/', recipeData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 생성에 실패했습니다.');
    }
  },

  /**
   * 기존 레시피에 recipe_data로 새 revision·recipe_run 생성.
   * POST /mes/dyeing/v2/recipe/recipe_id/{recipe_id}/revision
   * Body: { recipe_data: RecipeData, production_ids: number[] }
   */
  createRecipeRevisionWithRecipeData: async (recipeId, body) => {
    try {
      const response = await apiClient.post(
        `/mes/dyeing/v2/recipe/recipe_id/${recipeId}/revision`,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 리비전 추가에 실패했습니다.');
    }
  },

  /**
   * current_revision recipe_run.machine_no 만 갱신 (dyeing_process.machine_no 는 변경하지 않음).
   * PUT /mes/dyeing/v2/recipe/recipe_id/{recipe_id}/current_run/machine_no
   */
  updateCurrentRecipeRunMachineNo: async (recipeId, machineNo) => {
    try {
      const parsed =
        machineNo != null && machineNo !== ''
          ? parseInt(String(machineNo), 10)
          : null;
      const response = await apiClient.put(
        `/mes/dyeing/v2/recipe/recipe_id/${recipeId}/current_run/machine_no`,
        { machine_no: Number.isNaN(parsed) ? null : parsed },
        { headers: { 'Content-Type': 'application/json' } }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 M/C 할당에 실패했습니다.');
    }
  },

  /**
   * 자동 배치 폴링용 조회. current_revision recipe_run.machine_no 를 반환.
   * GET /mes/dyeing/v2/recipe/recipe_id/{recipe_id}/current_run/machine_no
   * 응답: { recipe_id, current_revision, recipe_run_id, machine_no }
   * machine_no == null → 아직 배정 전(처리 중) 또는 자동배정 skip.
   */
  getCurrentRecipeRunMachineNo: async (recipeId) => {
    try {
      const response = await apiClient.get(
        `/mes/dyeing/v2/recipe/recipe_id/${recipeId}/current_run/machine_no`
      );
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 M/C 배정 정보를 가져오지 못했습니다.');
    }
  },

  // 레시피 수정 (PUT recipe_id/{recipe_id}, body: RecipeUpdate = { recipe_data: RecipeData, target_revision: int })
  updateRecipe: async (recipeId, updatePayload) => {
    try {
      const response = await apiClient.put(`/mes/dyeing/v2/recipe/recipe_id/${recipeId}`, updatePayload, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 수정에 실패했습니다.');
    }
  },

  // 레시피 삭제
  deleteRecipe: async (recipeId) => {
    try {
      const response = await apiClient.delete(`/mes/dyeing/v2/recipe/recipe_id/${recipeId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 삭제에 실패했습니다.');
    }
  },

  /**
   * 지정한 production만 새 revision·recipe_run에 연결한다.
   * 끝난 run(상태 9, 10, 91, 92, 93)의 공정은 옮기지 않는다.
   * POST /java/mes/dyeing/v1/recipe/{recipeId}/revision
   * Body: { recipe_data, production_ids }
   */
  createRecipeRevisionForProductions: async (recipeId, body) => {
    try {
      const response = await apiClient.post(
        `/java/mes/dyeing/v1/recipe/${recipeId}/revision`,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 리비전 저장에 실패했습니다.');
    }
  },

  /**
   * POST /mes/dyeing/v2/recipe/new_revision/{recipe_id}
   * body의 production_ids를 읽지 않고, 레시피에 묶인 process를 전부 새 run으로 옮긴다.
   * 기존 레시피에 SPK를 붙일 때는 createRecipeRevisionForProductions를 쓴다.
   */
  createNewRevision: async (recipeId, payload) => {
    try {
      const response = await apiClient.post(
        `/mes/dyeing/v2/recipe/new_revision/${recipeId}`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 리비전 저장에 실패했습니다.');
    }
  },

  /** 최신 recipe_run 되돌리기 (최신 run 삭제, current_revision 1 감소) */
  rollbackLatestRecipeRun: async (recipeId) => {
    try {
      const response = await apiClient.post(`/mes/dyeing/v2/recipe/latest_recipe_run/${recipeId}/rollback`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 run 되돌리기에 실패했습니다.');
    }
  },

  // 염색 작업 요청 후보 목록 조회
  getDyeingRequestCandidates: async (params = {}) => {
    try {
      const response = await apiClient.get('/production/work_order/v1/dyeing_request/candidates', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 작업 요청 후보 목록을 가져오는데 실패했습니다.');
    }
  },

  // 염색 작업 요청 목록 조회 (기간)
  getDyeingRequestListByDateRange: async (params = {}) => {
    try {
      const response = await apiClient.get('/production/work_order/v1/dyeing_request/list_by_date_range', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 작업 요청 목록을 가져오는데 실패했습니다.');
    }
  },

  /**
   * 기간 내 dyeing_request 기준 LEFT JOIN dyeing_process (공정 없으면 요청만).
   * Query: start_date, end_date (필수)
   * Response: { dyeing_request_info, dyeing_process_info | null }[]
   */
  getDyeingProcessWorkListByDateRange: async (params = {}) => {
    try {
      const response = await apiClient.get('/mes/dyeing/v2/process/work_list_by_date_range', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 작업(요청·공정) 목록을 가져오는데 실패했습니다.');
    }
  },

  /**
   * 염색 작업 화면용 dyeing_request 목록 (offset 페이지네이션).
   * Query: start_date, end_date (ISO8601), item, color, dyeing_no_list,
   *   production_id_short, recipe_id_short, machine_no, dyeing_status, limit, offset
   * Response: { total, limit, offset, has_more, items }
   */
  getDyeingRequestForDyeingWork: async (params = {}) => {
    try {
      const response = await apiClient.get(
        '/mes/dyeing/v2/process/dyeing_request_for_dyeing_work',
        {
          params,
          // FastAPI List[int]: dyeing_no_list=1&dyeing_no_list=2 (axios 기본은 dyeing_no_list[]=…)
          paramsSerializer: serializeFastApiQueryParams
        }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 작업 요청 목록을 가져오는데 실패했습니다.');
    }
  },

  /**
   * dyeing_no_short 부분 일치(OR)로 dyeing_no 숫자 목록 조회.
   * Query: start_date, end_date, dyeing_no_query (쉼표 구분 검색어)
   */
  searchDyeingRequestsByDyeingNoQuery: async (params = {}) => {
    try {
      const response = await apiClient.get('/production/work_order/v1/dyeing_request/search', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 번호 검색에 실패했습니다.');
    }
  },

  /**
   * 기간 내 recipe_id_short 부분 일치(OR)로 recipe_id 숫자 목록 조회.
   * Query: start_date, end_date, recipe_id_query (쉼표 구분 검색어)
   */
  searchRecipeIdsByRecipeIdShortQuery: async (params = {}) => {
    try {
      const response = await apiClient.get('/mes/dyeing/v2/recipe/search_recipe_ids', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 ID 검색에 실패했습니다.');
    }
  },

  // dyeing_no 목록으로 염색 프로세스 조회
  getDyeingProcessByDyeingNo: async (dyeingNoList = []) => {
    try {
      const response = await apiClient.post('/mes/dyeing/v2/process/process_by_dyeing_no', dyeingNoList, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색번호별 프로세스 목록을 가져오는데 실패했습니다.');
    }
  },

  // 염색 작업 요청 생성
  createDyeingRequest: async (requestData) => {
    try {
      const response = await apiClient.post('/production/work_order/v1/dyeing_request/', requestData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 작업 요청 생성에 실패했습니다.');
    }
  },

  // 벤더 컨펌 이벤트 전송
  sendVendorConfirmEvent: async (eventData) => {
    try {
      const response = await apiClient.post('/mes/events/v1/', eventData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '벤더 컨펌 이벤트 전송에 실패했습니다.');
    }
  },

  // 벤더 컨펌 취소 이벤트 전송
  sendVendorCancelEvent: async (eventData) => {
    try {
      const response = await apiClient.post('/mes/events/v1/', eventData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '벤더 컨펌 취소 이벤트 전송에 실패했습니다.');
    }
  },

  // 벤더 컨펌 취소 이벤트 전송
  sendSwatchSendingEvent: async (eventData) => {
    try {
      const response = await apiClient.post('/mes/events/v1/', eventData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '벤더 컨펌 취소 이벤트 전송에 실패했습니다.');
    }
  },

  // 염색 작업 요청 수정
  updateDyeingRequest: async (dyeingNo, requestData) => {
    try {
      const response = await apiClient.put(`/production/work_order/v1/dyeing_request/item/${dyeingNo}`, requestData);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 작업 요청 수정에 실패했습니다.');
    }
  },

  // 염색 작업 요청 삭제
  deleteDyeingRequest: async (dyeingNo) => {
    try {
      const response = await apiClient.delete(`/production/work_order/v1/dyeing_request/item/${dyeingNo}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 작업 요청 삭제에 실패했습니다.');
    }
  },

  // 이벤트 생성 (FastAPI — swatch/vendor 등 비염색 배치 경로)
  createEvent: async (eventData) => {
    try {
      const response = await apiClient.post('/mes/events/v1/', eventData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '이벤트 생성에 실패했습니다.');
    }
  },

  /**
   * 염색 recipe_run 이벤트 (Java batch).
   * 200 sync / 202 accepted + job 폴링은 dyeingEventBatchApi.submitDyeingEventBatch 사용.
   */
  submitDyeingEventBatch: async (body) => {
    try {
      const response = await apiClient.post('/java/mes/events/v1/batch', body, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data, status: response.status };
    } catch (error) {
      return handleApiError(error, '염색 이벤트 전송에 실패했습니다.');
    }
  },

  getDyeingEventJob: async (jobId) => {
    try {
      const response = await apiClient.get(
        `/java/mes/events/v1/jobs/${encodeURIComponent(jobId)}`
      );
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 이벤트 job 조회에 실패했습니다.');
    }
  },

  // 염색 작업 현황 조회
  getDyeingStatus: async (params = {}) => {
    try {
      const response = await apiClient.get('/mes/dyeing/v2/process/list', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색 작업 현황을 가져오는데 실패했습니다.');
    }
  },

  // 레시피 검색 조회 (v2: start_date, end_date)
  getRecipeList: async (params = {}) => {
    try {
      const response = await apiClient.get('/mes/dyeing/v2/recipe/search', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 검색에 실패했습니다.');
    }
  },

  /**
   * dyeing_no_short에 해당하는 레시피 목록 조회 (List[RecipeResponse]).
   * 표시용 필드: recipe_id, dyeing_no_short_list
   */
  getRecipeByDyeingNo: async (dyeingNoShort) => {
    try {
      const response = await apiClient.get(`/mes/dyeing/v2/recipe/by_dyeing_no/${encodeURIComponent(dyeingNoShort)}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '염색번호별 레시피 목록을 가져오는데 실패했습니다.');
    }
  },

  // recipe_id 목록으로 recipe_id_short 조회
  getRecipeIdList: async (recipeIdList = []) => {
    try {
      const response = await apiClient.post('/mes/dyeing/v2/recipe/recipe_id_list', recipeIdList, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 축약 ID 목록을 가져오는데 실패했습니다.');
    }
  },

  /**
   * recipe_id 목록으로 current_revision recipe_data + recipe_runs 일괄 조회.
   * machine/status: 최신 recipe_run, boiling_start: runs 최소, boiling_end: runs 최대(상태 2,3,12,4,5,91,94 제외)
   */
  getRecipeDataList: async (recipeIdList = []) => {
    try {
      const response = await apiClient.post('/mes/dyeing/v2/recipe/recipe_data_list', recipeIdList, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 데이터 목록을 가져오는데 실패했습니다.');
    }
  },

  // 끓임 기록 조회
  getBoilingRecords: async (params = {}) => {
    try {
      const response = await apiClient.get('/mes/dyeing/v2/process/boiling-records', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '끓임 기록을 가져오는데 실패했습니다.');
    }
  },

  /**
   * 레시피 기준 염색 작업 현황 조회.
   * Query: start_date, end_date, recipe_ids (복수 가능 — FastAPI List[int])
   */
  getDyeingProductionByRecipe: async (params = {}) => {
    try {
      const { recipe_id_list, recipe_ids, ...rest } = params;
      const queryParams = { ...rest };
      const ids = recipe_ids ?? recipe_id_list;
      if (Array.isArray(ids) && ids.length > 0) {
        queryParams.recipe_ids = ids;
      }
      const response = await apiClient.get('/mes/dyeing/v2/process/process_by_recipe', {
        params: queryParams,
        paramsSerializer: Array.isArray(queryParams.recipe_ids) ? serializeFastApiQueryParams : undefined
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '레시피 기준 염색 작업 현황을 가져오는데 실패했습니다.');
    }
  },

  // 끓임 기록 수정 (boiling_start, boiling_end)
  modifyBoilingRecords: async (payload) => {
    try {
      const body = {
        recipe_id: payload.recipe_id,
        production_id_list: payload.production_id_list,
        boiling_start: payload.boiling_start ? payload.boiling_start.toISOString() : null,
        boiling_end: payload.boiling_end ? payload.boiling_end.toISOString() : null
      };
      const response = await apiClient.put('/mes/dyeing/v2/process/boiling-records', body, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '끓임 기록 수정에 실패했습니다.');
    }
  },

  /**
   * Reject된 염색 작업에 대해 동일 recipe_id, 새 recipe_run/recipe_revision으로 재작업 염색 요청 생성.
   * Backend: POST /mes/dyeing/v2/process/rework-from-reject
   * Request: { production_id: string, recipe_id: string|number }
   * Response: 생성된 염색 요청 목록 (dyeing_no, dyeing_no_short 등)
   */
  createReworkFromReject: async (payload) => {
    try {
      const response = await apiClient.post('/mes/dyeing/v2/process/rework-from-reject', payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '재작업 염색 요청 생성에 실패했습니다.');
    }
  },

  /**
   * 기계별 염색 완료 이력 조회 (UTC 구간).
   * @param {{ startDatetime: string, endDatetime: string, machineNos?: number[] }} params
   */
  getMachineQueueCompleted: async ({ startDatetime, endDatetime, machineNos = [] } = {}) => {
    try {
      const params = {
        start_datetime: startDatetime,
        end_datetime: endDatetime,
      };
      if (Array.isArray(machineNos) && machineNos.length > 0) {
        params.machine_no = machineNos;
      }
      const response = await apiClient.get('/java/mes/dyeing/v1/machine_queue/completed', {
        params,
        paramsSerializer: serializeFastApiQueryParams
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '기계별 염색 완료 이력을 가져오는데 실패했습니다.');
    }
  },

  /** 기계별 대기 큐 조회 (Java). machineNos 미지정 시 활성 기계 전체. entry에 is_delivery 포함 */
  getMachineQueues: async (machineNos = []) => {
    try {
      const params = {};
      if (Array.isArray(machineNos) && machineNos.length > 0) {
        params.machine_no = machineNos;
      }
      const response = await apiClient.get('/java/mes/dyeing/v1/machine_queue', {
        params,
        paramsSerializer: serializeFastApiQueryParams
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '기계별 대기 큐를 가져오는데 실패했습니다.');
    }
  },

  /** 기계별 대기 큐 entry hover 상세 (recipe 단위) */
  getMachineQueueRecipeDetail: async (recipeId, options = {}) => {
    try {
      const response = await apiClient.get(
        `/mes/dyeing/v2/machine_queue/recipe/${recipeId}`,
        { signal: options.signal }
      );
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return { success: false, canceled: true };
      }
      return handleApiError(error, '대기 큐 상세 정보를 가져오는데 실패했습니다.');
    }
  },

  /** 큐 순서 변경 / 기계 이동 (admin) */
  moveMachineQueueEntry: async (body) => {
    try {
      const response = await apiClient.post('/mes/dyeing/v2/machine_queue/move', body, {
        headers: { 'Content-Type': 'application/json' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '대기 순서 변경에 실패했습니다.');
    }
  },

  /**
   * 기계 대기 큐 urgent 표시 덮어쓰기 (admin, dyeing).
   * urgentOverride: "delivery" | "urgent" | "delayed" | "normal" | null
   * null 해제는 JSON 키를 유지해야 하므로 body에서 키를 빼지 않는다.
   */
  patchMachineQueueUrgentOverride: async (recipeId, urgentOverride) => {
    try {
      const response = await apiClient.patch(
        `/java/mes/dyeing/v1/machine_queue/${recipeId}/urgent_override`,
        { urgent_override: urgentOverride },
        { headers: { 'Content-Type': 'application/json' } }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '긴급 표시 변경에 실패했습니다.');
    }
  }
}; 